import { useMemo } from "react";
import { useSelector } from "react-redux";

const EMPTY = [];

// Pull a human-readable table name out of whatever shape a TFL entry takes:
// a bare string, or an object keyed by one of the common name fields.
const asName = item => {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    return (
      item.name ??
      item.table_name ??
      item.tableName ??
      item.title ??
      item.label ??
      item.value ??
      null
    );
  }
  return null;
};

// The SCS tfl-content endpoint shape isn't fixed, so normalize defensively:
//  - a flat array (of strings or objects)
//  - an object exposing named arrays (tables/figures/listings/…)
//  - failing that, any array-valued property on the object
// Everything collapses to a de-duplicated list of table-name strings.
export const normalizeScsTflContent = raw => {
  if (!raw) return [];

  const collect = value => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(asName).filter(Boolean);
    if (typeof value === "object") {
      const known = [
        "tables",
        "figures",
        "listings",
        "tfl",
        "tfl_content",
        "tflContent",
        "content",
        "data",
        "items",
      ];
      const hits = [];
      known.forEach(key => {
        if (Array.isArray(value[key])) hits.push(...collect(value[key]));
      });
      if (hits.length) return hits;
      // Fallback: any array-valued property.
      Object.values(value).forEach(v => {
        if (Array.isArray(v)) hits.push(...collect(v));
      });
      return hits;
    }
    return [];
  };

  return [...new Set(collect(raw))];
};

/**
 * Read-only consumer of the `scsData` slice. Exposes the SCS TFL content as
 * ready-to-use dropdown state, mirroring the `sharedTableData` contract the
 * Data Mapping dropdowns already expect. Fetching/polling is owned by
 * CsrProvider (getSafetyDocuments -> fetchScsTflContent); this hook only reads.
 */
export const useScsTableOptions = () => {
  const bucket = useSelector(state => state.scsData.tflContent);

  const tableNames = useMemo(
    () => (bucket.fetched ? normalizeScsTflContent(bucket.data) : EMPTY),
    [bucket.fetched, bucket.data]
  );

  const tableOptions = useMemo(
    () =>
      tableNames.map(name => ({
        value: name,
        label: name,
        valueHelper: name,
      })),
    [tableNames]
  );

  return {
    tableNames,
    tableOptions,
    isDataReady: bucket.fetched && !bucket.error && tableOptions.length > 0,
    isLoadingData: bucket.loading,
    dataError: bucket.error,
  };
};
